/**
 * SearchModal - Cmd+K Spotlight-style global search (desktop)
 *
 * Uses cmdk for UI shell (keyboard nav, a11y, IME composition).
 * The result list itself — grouping, selection, analytics — lives in
 * components/search/SearchResults.jsx so the phone's search page
 * (MobileSearchPage) shares it instead of drifting.
 *
 * Searches across articles, highlights, and cards.
 *
 * Architecture: Designed to evolve into a full command palette (Plan D).
 * - Command registry pattern for easy action additions (in SearchResults)
 * - Result types as discriminated union for extensible rendering
 * - Search logic separated in useSearchIndex hook
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from '@legendapp/state/react';
import { Command } from 'cmdk';
import { Search } from 'lucide-react';
import { ui$, closeSearchModal } from '../store';
import { Dialog, ICON_SIZE } from '../design-system';
import SearchResults from './search/SearchResults';
import { trackEvent } from '../lib/analytics';
import './SearchModal.css';

const SearchModal = () => {
  const { t } = useTranslation(['search', 'common', 'sidebar']);
  const isOpen = useSelector(() => ui$.searchModalOpen.get());
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceRef = useRef(null);
  const searchInputRef = useRef(null);

  // Debounce search query (150ms)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(query);
    }, 150);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // Reset query when modal closes; fire open event when it opens
  useEffect(() => {
    if (isOpen) {
      trackEvent('search_modal_opened');
      return undefined;
    }

    const frame = requestAnimationFrame(() => {
      setQuery('');
      setDebouncedQuery('');
    });
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={closeSearchModal}
      ariaLabel={t('common:shortcuts.search')}
      showClose={false}
      size="xl"
      initialFocusRef={searchInputRef}
      className="search-command-dialog"
    >
      <Command
        className="search-command"
        label={t('common:shortcuts.search')}
        shouldFilter={false}
        loop
      >
        <div className="search-modal-input-wrapper">
          <Search size={ICON_SIZE.md} className="search-modal-input-icon" aria-hidden="true" />
          <Command.Input
            ref={searchInputRef}
            value={query}
            onValueChange={setQuery}
            placeholder={t('search:placeholder')}
            className="search-modal-input"
          />
          {/* Keyboard key labels are platform input, not localized copy. */}
          {/* eslint-disable-next-line i18next/no-literal-string */}
          <kbd className="search-modal-esc-hint">Esc</kbd>
        </div>

        <SearchResults debouncedQuery={debouncedQuery} onDismiss={closeSearchModal} />
      </Command>
    </Dialog>
  );
};

export default SearchModal;
